import { IOutputItem } from 'Modules/Studio/Questions/EditQuestion/SubQuestions/redux/constants/subQuestions.interface';
import {
  ICalcs,
  IQuestionProperty,
  TAllWindowFunctionDeps,
  TPropertyDepWindowFunctions,
} from '@redux/Studio/QueryCreation/constants/queryCreation.interface';
import { IWindowFunctionItem } from '../../WindowFunctionsModal/constants/interfaces/windowFunctions.interface';

/**
 * Generic callback to find the property dependency within the window functions
 * @param windowFunctions
 * @param prop
 * @returns
 */
const getIsPropUsedInWF = (
  windowFunctions: IWindowFunctionItem[],
  prop: IOutputItem | ICalcs,
  isCalc: boolean
): IWindowFunctionItem[] => {
  const { conceptId, uri } = prop as IOutputItem;
  const { id } = prop as ICalcs;

  return windowFunctions.filter((wf) => {
    const usedInPartition = wf.partition?.find((partition) => {
      if (isCalc) {
        return partition.calcId === id;
      }
      return partition.concept === conceptId && partition.property === uri;
    });
    const usedInRuntimeSort = (wf?.sorting || []).find((sort) => {
      if (isCalc) {
        return sort.calcId === id;
      }
      return sort.concept === conceptId && sort.property === uri;
    });

    return usedInPartition || usedInRuntimeSort;
  });
};

/**
 * iterate all window functions for the question to see if the property to be deleted is being used in a partition or sort
 * @param allPropertyWindowFunctions
 * @param prop
 * @returns TAllWindowFunctions[]
 */
const reduceWfs = (
  allPropertyWindowFunctions: TPropertyDepWindowFunctions[],
  prop: IOutputItem,
  isCalc: boolean
): TPropertyDepWindowFunctions[] => {
  // reduce window functions to those that are referencing the prop that is passed in
  const WFDeps: TPropertyDepWindowFunctions[] = allPropertyWindowFunctions.reduce(
    (acc: TPropertyDepWindowFunctions[], wfItem) => {
      // reduce properties, to those using wf's w/the prop that's passed in
      const usedInProperty: IQuestionProperty[] = wfItem.properties.reduce(
        (acc: IQuestionProperty[], wfProp) => {
          // filter ONLY the window functions that are dependencies
          const propInWFs = getIsPropUsedInWF(wfProp.windowFunctions, prop, isCalc);

          // If the property has window functions that are dependencies, add it to the array
          if (propInWFs.length > 0) {
            return [
              ...acc,
              {
                ...wfProp,
                windowFunctions: propInWFs,
              },
            ];
          }
          return acc;
        },
        []
      );

      if (usedInProperty.length > 0) {
        // modify the properties to ONLY those used by the prop that is passed in
        return [...acc, { ...wfItem, properties: usedInProperty }];
      }
      return acc;
    },
    []
  );

  return WFDeps;
};

/**
 * Iterate the calcsList that has window functions to see if the property to be deleted is being used in a partition or sort
 * @param calcsList
 * @param prop
 * @returns
 */
const filterCalcsWithWFs = (calcsList: ICalcs[], prop: IOutputItem, isCalc: boolean): ICalcs[] => {
  return calcsList.reduce((acc: ICalcs[], calc) => {
    const usedInProperty = getIsPropUsedInWF(
      calc?.windowFunctions || [],
      prop as IOutputItem,
      isCalc
    );

    if (usedInProperty.length > 0) {
      return [
        ...acc,
        {
          ...calc,
          windowFunctions: usedInProperty,
        },
      ];
    }

    return acc;
  }, []);
};

/**
 *
 * @param props
 * @param allPropertyWindowFunctions
 * @returns window functions that have a dependency on the property being removed
 */
export const getWFDeps = (
  props: IOutputItem[],
  allPropertyWindowFunctions: TPropertyDepWindowFunctions[],
  allCalcWindowFunctions: ICalcs[],
  isCalc: boolean
): TAllWindowFunctionDeps => {
  // iterate all props passed in, to get the window function deps
  const WFDeps = props.reduce(
    (propAcc: TAllWindowFunctionDeps, prop) => {
      const propertyWFDeps = reduceWfs(allPropertyWindowFunctions, prop, isCalc);
      const calcWFDeps = filterCalcsWithWFs(allCalcWindowFunctions, prop, isCalc);

      return {
        ...propAcc,
        properties: [...propAcc.properties, ...propertyWFDeps],
        calcs: [...propAcc.calcs, ...calcWFDeps],
      };
    },
    { properties: [], calcs: [] }
  );

  return WFDeps;
};
